'use client'

import React, { useState, useCallback, useEffect, useLayoutEffect, useRef, useId } from 'react'
import { useTranslations } from 'next-intl'
import Icon from '../../atoms/Icon/Icon.component'
import Button from '../../atoms/Button/Button.component'
import { ResourceHubFiltersModalProps } from '../../../models/organisms/ResourceHub'
import { SortOrder } from '../../../types/ResourceHub'
import {
  FilterSelectCheckbox,
  FilterSelectCheckboxInput,
  FilterSelectOption,
  FilterSelectOptionLabel,
  FilterSelectDivider,
  FilterSelectSelectedCount,
} from '../../molecules/FilterSelect/FilterSelect.styles'
import * as S from './ResourceHub.styles'

const SORT_OPTIONS: SortOrder[] = ['asc', 'desc']

function getFocusableElements(root: HTMLElement): HTMLElement[] {
  const selector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(', ')

  return Array.from(root.querySelectorAll<HTMLElement>(selector)).filter((el) => {
    if (el.closest('[hidden]')) return false
    return true
  })
}

function ResourceHubFiltersModal({
  isOpen,
  filters,
  activeFilters,
  onFilterToggle,
  onFilterClear,
  onClearAllFilters,
  sortOrder,
  onSortChange,
  totalItems,
  colorScheme,
  onClose,
  returnFocusRef,
}: ResourceHubFiltersModalProps) {
  const t = useTranslations('resourceHub')
  const [expandedPanel, setExpandedPanel] = useState<string | null>(null)
  const dialogRef = useRef<HTMLDivElement>(null)
  const titleRef = useRef<HTMLHeadingElement>(null)
  const onCloseRef = useRef(onClose)
  const dialogReactId = useId()
  const titleId = `${dialogReactId}-title`

  onCloseRef.current = onClose

  const togglePanel = useCallback((key: string) => {
    setExpandedPanel((prev) => (prev === key ? null : key))
  }, [])

  useLayoutEffect(() => {
    const el = dialogRef.current
    if (!el) return
    if (isOpen) {
      el.removeAttribute('inert')
    } else {
      el.setAttribute('inert', '')
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return

    const dialog = dialogRef.current
    const returnTarget = returnFocusRef.current

    const focusTitle = () => {
      titleRef.current?.focus()
    }

    const rafId = requestAnimationFrame(focusTitle)

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.stopPropagation()
        onCloseRef.current(false)
        return
      }

      if (event.key !== 'Tab' || !dialog) return

      const focusables = getFocusableElements(dialog)
      if (focusables.length === 0) return

      const first = focusables[0]
      const last = focusables[focusables.length - 1]
      const active = document.activeElement as HTMLElement | null

      if (event.shiftKey) {
        if (active === first) {
          event.preventDefault()
          last.focus()
        }
      } else if (active === last) {
        event.preventDefault()
        first.focus()
      }
    }

    dialog?.addEventListener('keydown', handleKeyDown)

    return () => {
      cancelAnimationFrame(rafId)
      dialog?.removeEventListener('keydown', handleKeyDown)
      returnTarget?.focus?.()
    }
  }, [isOpen, returnFocusRef])

  return (
    <S.FiltersModal
      ref={dialogRef}
      $isOpen={isOpen}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-hidden={!isOpen}
    >
      <S.FiltersModalHeader>
        <S.FiltersModalTitle id={titleId} tabIndex={-1} ref={titleRef}>
          {t('filtersTitle')}
        </S.FiltersModalTitle>
        <Button
          variant="secondary"
          iconEnd="x-mark"
          ariaLabel={t('closeFilters')}
          handleClick={() => onClose(false)}
        />
      </S.FiltersModalHeader>

      <S.FiltersModalBody>
        {filters &&
          Object.entries(filters).map(([key, options]) => {
            const selectedValues = (activeFilters[key] as string[] | undefined) ?? []
            const panelOpen = expandedPanel === key
            const allSelected =
              options.length > 0 && options.every((opt: string) => selectedValues.includes(opt))
            const bodyId = `${dialogReactId}-body-${key}`

            const handleToggleAll = () => {
              if (allSelected) {
                onFilterClear(key)
              } else {
                options.forEach((opt: string) => {
                  if (!selectedValues.includes(opt)) {
                    onFilterToggle(key, opt)
                  }
                })
              }
            }

            return (
              <S.FiltersModalAccordionPanel key={key}>
                <S.FiltersModalAccordionHeader
                  type="button"
                  $isOpen={panelOpen}
                  onClick={() => togglePanel(key)}
                  aria-expanded={panelOpen}
                  aria-controls={bodyId}
                >
                  <span>
                    {t(`filters.${key}.label`)}
                    {selectedValues.length > 0 && (
                      <FilterSelectSelectedCount colorScheme={colorScheme} aria-hidden>
                        {selectedValues.length}
                      </FilterSelectSelectedCount>
                    )}
                  </span>
                  <Icon name="chevron-down" size="small" aria-hidden />
                </S.FiltersModalAccordionHeader>

                <S.FiltersModalAccordionBody id={bodyId} hidden={!panelOpen}>
                  <FilterSelectOption as="label">
                    <FilterSelectCheckboxInput checked={allSelected} onChange={handleToggleAll} />
                    <FilterSelectCheckbox aria-hidden $checked={allSelected} />
                    <FilterSelectOptionLabel>{t('filters.selectAll')}</FilterSelectOptionLabel>
                  </FilterSelectOption>

                  <FilterSelectDivider />

                  {options.map((option: string) => {
                    const checked = selectedValues.includes(option)
                    return (
                      <FilterSelectOption key={option} as="label">
                        <FilterSelectCheckboxInput
                          checked={checked}
                          onChange={() => onFilterToggle(key, option)}
                        />
                        <FilterSelectCheckbox aria-hidden $checked={checked} />
                        <Icon name={option} size="small" aria-hidden />
                        <FilterSelectOptionLabel>
                          {t(`filters.${key}.options.${option}`)}
                        </FilterSelectOptionLabel>
                      </FilterSelectOption>
                    )
                  })}
                </S.FiltersModalAccordionBody>
              </S.FiltersModalAccordionPanel>
            )
          })}

        <S.FiltersModalAccordionPanel>
          <S.FiltersModalAccordionHeader
            type="button"
            $isOpen={expandedPanel === 'sort'}
            onClick={() => togglePanel('sort')}
            aria-expanded={expandedPanel === 'sort'}
            aria-controls={`${dialogReactId}-body-sort`}
          >
            {t('sort.label')}
            <Icon name="chevron-down" size="small" aria-hidden />
          </S.FiltersModalAccordionHeader>

          <S.FiltersModalAccordionBody id={`${dialogReactId}-body-sort`} hidden={expandedPanel !== 'sort'}>
            {SORT_OPTIONS.map((option) => (
              <S.FiltersModalSortOption
                key={option}
                type="button"
                $active={sortOrder === option}
                onClick={() => onSortChange(option)}
              >
                {t(`sort.${option}`)}
              </S.FiltersModalSortOption>
            ))}
          </S.FiltersModalAccordionBody>
        </S.FiltersModalAccordionPanel>
      </S.FiltersModalBody>

      <S.FiltersModalFooter colorScheme={colorScheme}>
        <Button
          variant="secondary"
          label={t('clearAllFilters')}
          iconStart="trash"
          handleClick={onClearAllFilters}
        />
        <Button
          variant="primary"
          label={t('showResults', { count: totalItems })}
          handleClick={() => onClose(true)}
        />
      </S.FiltersModalFooter>
    </S.FiltersModal>
  )
}

export default ResourceHubFiltersModal
